import { useCallback, useMemo } from "react";
import SetSelector, { type SetSelectorOption } from "components/Inputs/SetSelector";
import { useChartSetSelectContext } from "components/charting/contexts/ChartSetSelectContext";
import { useChartCompareSetSelectContext } from "components/charting/contexts/ChartCompareSetSelectContext";
import { useChartProfileSelectContext } from "components/charting/contexts/ChartProfileSelectContext";
import { useChartCompareProfileSelectContext } from "components/charting/contexts/ChartCompareProfileSelectContext";
import { useTranslation } from "react-i18next";
import type { AutocompleteChangeDetails, AutocompleteChangeReason } from "@mui/material/Autocomplete";
import LoadVelocityProfileHelpers from "helpers/LoadVelocityProfileHelpers";
import { useChartSetSelectStatusContext } from "components/charting/contexts/ChartSetSelectStatusContext";
import { useChartProfileSelectStatusContext } from "components/charting/contexts/ChartProfileSelectStatusContext";
import useStatusBuilder from "hooks/useStatusBuilder";
import useProfileTypeQueryState, { ProfileType } from "components/charting/hooks/useProfileTypeQueryState";
import ProfilingVelocityOnboardingTooltip from "../components/ProfilingVelocityOnboardingTooltip";
import LocalStorageHelper from "helpers/LocalStorageHelper";
import useVelocityOnBoarding from "hooks/useVelocityOnBoarding";

export interface AthleteProfileChartProfilingSetSelectorProps {}

function AthleteProfileChartProfilingSetSelector(props: AthleteProfileChartProfilingSetSelectorProps) {
  const { t } = useTranslation();
  const { value, options, onChange } = useChartSetSelectContext();
  const { value: profiles, options: profileOptions, onChange: onProfilesChange } = useChartProfileSelectContext();
  const { value: selectedCompareSets } = useChartCompareSetSelectContext();
  const { value: selectedCompareProfiles } = useChartCompareProfileSelectContext();
  const [profileType] = useProfileTypeQueryState();
  const { open: onboardingOpen } = useVelocityOnBoarding(LocalStorageHelper.keys.showAthleteProfilingVelocityTooltip);
  const isVelocityProfile = profileType === ProfileType.Velocity;

  const setStatus = useChartSetSelectStatusContext();
  const profileStatus = useChartProfileSelectStatusContext();

  const status = useStatusBuilder(useMemo(() => [setStatus, profileStatus], [setStatus, profileStatus]));

  const combinedOptions = useMemo<SetSelectorOption[]>(
    () => [...profileOptions, ...options],
    [profileOptions, options]
  );
  const combinedValue = useMemo<SetSelectorOption[]>(() => [...profiles, ...value], [profiles, value]);

  const getOptionDisabled = useCallback(
    (option: SetSelectorOption) => {
      if (LoadVelocityProfileHelpers.isLoadVelocityProfile(option)) {
        return selectedCompareProfiles.includes(option);
      }
      return selectedCompareSets.includes(option);
    },
    [selectedCompareProfiles, selectedCompareSets]
  );

  // Comparison picks can't be baselines too, but still show as picked.
  const getOptionChecked = useCallback(
    (option: SetSelectorOption) =>
      LoadVelocityProfileHelpers.isLoadVelocityProfile(option)
        ? selectedCompareProfiles.includes(option)
        : selectedCompareSets.includes(option),
    [selectedCompareProfiles, selectedCompareSets]
  );

  const TextFieldProps = useMemo(
    () => ({
      label: t("athlete_profile_chart_set_selector.set_select_label"),
      placeholder: combinedValue.length ? undefined : t("global.search_sets"),
      "data-highlight": onboardingOpen && isVelocityProfile,
    }),
    [combinedValue.length, isVelocityProfile, onboardingOpen, t]
  );

  const handleChange = useCallback(
    (
      _: unknown,
      selected: SetSelectorOption[],
      _reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<SetSelectorOption>
    ) => {
      const { profiles, sets } = LoadVelocityProfileHelpers.partitionSelection(selected, details?.option);
      onProfilesChange(profiles);
      onChange(sets);
    },
    [onChange, onProfilesChange]
  );

  return (
    <ProfilingVelocityOnboardingTooltip disabled={!isVelocityProfile}>
      <SetSelector<SetSelectorOption>
        value={combinedValue}
        options={combinedOptions}
        onChange={handleChange}
        TextFieldProps={TextFieldProps}
        getOptionDisabled={getOptionDisabled}
        getOptionChecked={getOptionChecked}
        status={status}
        emptyVelocityProfileNotice={isVelocityProfile}
      />
    </ProfilingVelocityOnboardingTooltip>
  );
}

export default AthleteProfileChartProfilingSetSelector;
